/**
 * ResultsPage — Assessment results display
 *
 * Shows dimension scores with character commentary and
 * recommended starting modules.
 */

import React from 'react';
import { Link, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';

import { useProgress } from '../context/ProgressContext';
import {
  SCORING_DIMENSIONS,
  getResultObservations,
} from '../data/assessment';
import { getCharacterById } from '../data/characters';
import CharacterDialogue from '../components/characters/CharacterDialogue';
import Card from '../components/shared/Card';
import Button from '../components/shared/Button';

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.12, duration: 0.5 },
  }),
};

const ResultsPage = () => {
  const { assessmentResults, hasCompletedAssessment } = useProgress();

  if (!hasCompletedAssessment || !assessmentResults) {
    return <Navigate to="/assessment" replace />;
  }

  const { scores, recommended, answers } = assessmentResults;
  // "Dread. A physical heaviness." or "keeping me up at night": the two
  // answers that say this is more than nerves. Those readers get a plain
  // pointer to real help as well as a module to start with.
  const heavy = answers?.['weight-of-it'] === 'a' || answers?.['the-situation'] === 'a';
  const observations = getResultObservations(scores);

  return (
    <>
      <Helmet>
        <title>Your Results | Finding Your Voice</title>
      </Helmet>

      <div className="max-w-3xl mx-auto px-4 py-10 space-y-10">
        {/* Header */}
        <motion.div
          className="text-center"
          initial="hidden"
          animate="visible"
          variants={fadeUp}
        >
          <h1 className="font-display text-3xl md:text-4xl font-bold text-courtyard-stone">
            Your Results
          </h1>
          <p className="mt-2 text-courtyard-stone/80">
            Here is what Philemon, Xenia, and Theron noticed. The scores are a
            rough reading of your own answers, there to suggest where to begin.
            They are not a test or a diagnosis.
          </p>
        </motion.div>

        {/* Dimension Scores */}
        <div className="space-y-6">
          {observations.map((obs, i) => {
            const dimension = SCORING_DIMENSIONS.find(
              (d) => d.id === obs.dimension
            );
            const character = getCharacterById(obs.character);

            return (
              <motion.div
                key={obs.dimension}
                initial="hidden"
                animate="visible"
                variants={fadeUp}
                custom={i + 1}
              >
                <Card
                  variant={obs.character}
                  className="p-5 space-y-3"
                >
                  <div className="flex items-center justify-between">
                    <h3 className="font-display font-bold text-courtyard-stone">
                      {dimension?.label}
                    </h3>
                    <span className="text-sm font-bold text-courtyard-stone/80">
                      {obs.score}/10
                    </span>
                  </div>

                  {/* Score Bar */}
                  <div className="h-2 bg-courtyard-stone/10 rounded-full overflow-hidden">
                    <motion.div
                      className="h-full rounded-full"
                      style={{ backgroundColor: character?.colour || '#999' }}
                      initial={{ width: 0 }}
                      animate={{ width: `${(obs.score / 10) * 100}%` }}
                      transition={{ duration: 0.8, delay: i * 0.12 + 0.3 }}
                    />
                  </div>

                  {/* Character Observation */}
                  <CharacterDialogue
                    characterId={obs.character}
                    speaker={character?.name}
                    text={obs.observation}
                  />

                  <p className="text-sm text-courtyard-stone/80 leading-relaxed">
                    {obs.guidance}
                  </p>
                </Card>
              </motion.div>
            );
          })}
        </div>

        {/* Recommended Modules */}
        {recommended && (
          <motion.section
            initial="hidden"
            animate="visible"
            variants={fadeUp}
            custom={observations.length + 1}
            className="text-center"
          >
            <Card className="p-8 bg-courtyard-gold/5 border-courtyard-gold/20">
              <h2 className="font-display text-xl font-bold text-courtyard-stone mb-2">
                Your Recommended Starting Point
              </h2>
              <p className="text-courtyard-stone/80 mb-6">
                Based on your responses, we suggest beginning with{' '}
                <strong>{recommended.label}</strong>.
              </p>
              <div className="flex flex-col sm:flex-row gap-3 justify-center">
                <Link to={`/evenings/${recommended.primary}`}>
                  <Button variant="primary" size="lg">
                    Start There
                  </Button>
                </Link>
                <Link to="/evenings">
                  <Button variant="secondary">
                    View All Evenings
                  </Button>
                </Link>
              </div>
            </Card>
          </motion.section>
        )}

        {heavy && (
          <Card className="p-6 border-courtyard-aegean/30 bg-courtyard-aegean/5">
            <h2 className="font-display text-lg font-bold text-courtyard-stone mb-2">
              If this is heavier than nerves
            </h2>
            <p className="text-courtyard-stone/90 leading-relaxed">
              You told the guides this one weighs on you. If the dread is costing
              you sleep, or it reaches well beyond speaking, a doctor or a
              psychologist can help with that directly, and this app is no
              substitute for them. The{' '}
              <Link to="/resources" className="underline underline-offset-4 font-semibold">
                Resources page
              </Link>{' '}
              lists support lines for Australia, New Zealand, India, the UK,
              Ireland, Canada and the US.
            </p>
          </Card>
        )}

        {/* Retake */}
        <motion.div
          className="text-center"
          initial="hidden"
          animate="visible"
          variants={fadeUp}
          custom={observations.length + 2}
        >
          <Link to="/assessment">
            <Button variant="ghost" size="sm">
              Retake Assessment
            </Button>
          </Link>
        </motion.div>
      </div>
    </>
  );
};

export default ResultsPage;
